import { useEffect, useState } from 'react';
import { UserApi } from '../service';
import { canManageUserAccounts } from '../constants/roles';
import {
  collectDeletedEmailsFromSharePools,
  filterShareSuggestionEmails,
  mergeDeletedEmailSets,
} from '../utils/userPresence';

type SharePool = readonly unknown[] | null | undefined;

export function useShareSuggestionDeletedEmails(
  active: boolean,
  userApi: UserApi,
  userRole: string | undefined,
  sharePools: SharePool[]
) {
  const [apiDeletedEmails, setApiDeletedEmails] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    if (!active) {
      setApiDeletedEmails(new Set());
      return;
    }

    if (!canManageUserAccounts(userRole)) {
      setApiDeletedEmails(new Set());
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const response = await userApi.apiUserGet({ credentials: 'include' });
        if (cancelled) return;
        const next = new Set<string>();
        for (const user of response.data?.users ?? []) {
          if (user.isDeleted && user.email) {
            next.add(String(user.email).trim().toLowerCase());
          }
        }
        setApiDeletedEmails(next);
      } catch {
        if (!cancelled) setApiDeletedEmails(new Set());
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [active, userApi, userRole]);

  const poolDeletedEmails = collectDeletedEmailsFromSharePools(...sharePools);
  const deletedEmailSet = mergeDeletedEmailSets(poolDeletedEmails, apiDeletedEmails);

  const filterSuggestionEmails = (emails: string[]) =>
    filterShareSuggestionEmails(emails, deletedEmailSet);

  return { deletedEmailSet, filterSuggestionEmails };
}
